import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Banknote,
  CheckCircle,
  IndianRupee,
  Shield,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { getExchangeHref, getExchangeUrlDisplay } from '@/config/site';

const EXCHANGE_URL = getExchangeHref();
const EXCHANGE_URL_LABEL = getExchangeUrlDisplay();

const depositSteps = [
  'Transfer INR via bank or UPI using our payment details',
  'Submit your UTR and payment proof in the exchange wallet',
  'After admin review, BZX is credited to your spot balance',
];

const withdrawSteps = [
  'Sell BZX from your wallet — balance is reserved instantly',
  'Request an INR payout to your linked bank or UPI',
  'Receive INR after review, with payout reference on record',
];

const highlights = [
  { icon: IndianRupee, label: 'INR-native flows', desc: 'Deposit & withdraw in rupees' },
  { icon: Shield, label: 'Reviewed payouts', desc: 'Manual verification for safety' },
  { icon: Clock, label: 'Track in wallet', desc: 'Full history in one place' },
];

export const InrFiat = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="inr-fiat"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden bg-[#0a0b0d]"
      data-testid="inr-fiat-section"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#9C7941]/6 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] bg-[#22c55e]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 bg-[#9C7941]/10 border border-[#9C7941]/30 px-4 py-2 rounded-full text-sm text-[#EBD38D] font-medium mb-5">
            <IndianRupee size={16} />
            India · INR on BITZX Exchange
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Deposit &amp; withdraw in
            <span className="text-[#EBD38D]"> Indian Rupees</span>
          </h2>
          <p className="text-[#9A9B9F] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            BITZX Exchange supports INR deposits and withdrawals for eligible Indian users.
            Fund your account with Indian Rupees, trade BZX, and cash out back to your bank or UPI —
            all tracked in your wallet with clear status updates.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 mb-12 max-w-4xl mx-auto">
          {highlights.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
              className="flex flex-col items-center text-center p-5 rounded-2xl bg-[#12141a]/80 border border-[#2a2d35] hover:border-[#9C7941]/40 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9C7941]/20 to-[#EBD38D]/10 flex items-center justify-center mb-3">
                <item.icon size={22} className="text-[#EBD38D]" />
              </div>
              <p className="text-white font-bold text-sm mb-1">{item.label}</p>
              <p className="text-[#6B6D75] text-xs">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative h-full bg-gradient-to-br from-[#12141a] to-[#15171c] border border-[#2a2d35] rounded-3xl p-8 md:p-10 overflow-hidden group hover:border-[#9C7941]/40 transition-colors"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(34,197,94,0.08),transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-green-500/10 border border-green-500/25 flex items-center justify-center">
                  <ArrowDownCircle size={24} className="text-green-400" />
                </div>
                <div>
                  <p className="text-green-400 text-xs font-bold uppercase tracking-wider">Deposit</p>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Add INR → get BZX</h3>
                </div>
              </div>
              <p className="text-[#8A8B90] text-sm md:text-base leading-relaxed mb-6">
                Buy crypto exposure by depositing Indian Rupees. Submit your transfer details and
                proof; once approved, BZX is added to your spot wallet so you can trade immediately.
              </p>
              <ul className="space-y-3 mb-8">
                {depositSteps.map((step) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-[#D5D5D0]">
                    <CheckCircle size={16} className="text-[#EBD38D] flex-shrink-0 mt-0.5" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
              <motion.a
                href={EXCHANGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9C7941] to-[#EBD38D] text-[#0a0b0d] font-bold px-6 py-3.5 rounded-xl shadow-[0_0_20px_rgba(156,121,65,0.25)] hover:shadow-[0_0_28px_rgba(156,121,65,0.4)] transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="inr-deposit-cta"
              >
                Deposit on Exchange
                <ArrowRight size={16} />
              </motion.a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative h-full bg-gradient-to-br from-[#12141a] to-[#15171c] border border-[#2a2d35] rounded-3xl p-8 md:p-10 overflow-hidden group hover:border-[#9C7941]/40 transition-colors"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(156,121,65,0.12),transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-[#9C7941]/15 border border-[#9C7941]/30 flex items-center justify-center">
                  <ArrowUpCircle size={24} className="text-[#EBD38D]" />
                </div>
                <div>
                  <p className="text-[#EBD38D] text-xs font-bold uppercase tracking-wider">Withdraw</p>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Sell BZX → receive INR</h3>
                </div>
              </div>
              <p className="text-[#8A8B90] text-sm md:text-base leading-relaxed mb-6">
                Convert your BZX balance back to Indian Rupees. Your BZX is reserved when you
                submit a payout request; INR is sent to your verified bank or UPI after admin approval.
              </p>
              <ul className="space-y-3 mb-8">
                {withdrawSteps.map((step) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-[#D5D5D0]">
                    <CheckCircle size={16} className="text-[#EBD38D] flex-shrink-0 mt-0.5" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
              <motion.a
                href={EXCHANGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9C7941] to-[#EBD38D] text-[#0a0b0d] font-bold px-6 py-3.5 rounded-xl shadow-[0_0_20px_rgba(156,121,65,0.25)] hover:shadow-[0_0_28px_rgba(156,121,65,0.4)] transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="inr-withdraw-cta"
              >
                Withdraw on Exchange
                <ArrowRight size={16} />
              </motion.a>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="relative rounded-2xl border border-[#9C7941]/25 bg-gradient-to-r from-[#12141a] via-[#15171c] to-[#12141a] px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div className="flex items-start gap-4">
            <Banknote size={28} className="text-[#EBD38D] flex-shrink-0 mt-0.5" />
            <p className="text-[#8A8B90] text-sm md:text-base leading-relaxed">
              <span className="text-white font-semibold">Available on the live exchange.</span>
              {' '}
              Sign in at {EXCHANGE_URL_LABEL}, complete KYC where required, and manage INR deposits,
              payouts, and full transaction history under Wallet → History → INR history.
            </p>
          </div>
          <motion.a
            href={EXCHANGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-[#0a0b0d] font-bold px-5 py-3 rounded-xl bg-gradient-to-r from-[#9C7941] to-[#EBD38D] flex-shrink-0"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Open {EXCHANGE_URL_LABEL}
            <ArrowRight size={16} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default InrFiat;
